import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api';

const ResetPassword = () => {
  const { token } = useParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const isTokenPresent = useMemo(() => typeof token === 'string' && token.length >= 20, [token]);

  const validatePassword = (value) => {
    const passwordRegex = /^(?=.*[A-Z])(?=.*\d)[A-Za-z\d@$!%*?&]{8,}$/;
    return passwordRegex.test(value);
  };

  // Real-time passive checks for criteria indicators
  const criteria = {
    hasLength: password.length >= 8,
    hasUpper: /[A-Z]/.test(password),
    hasNumber: /\d/.test(password),
    matches: password === confirmPassword && confirmPassword.length > 0
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isTokenPresent) {
      setMessage({ type: 'error', text: 'Invalid or missing reset token.' });
      return;
    }

    if (!password || !confirmPassword) {
      setMessage({ type: 'error', text: 'Please enter and confirm your new password.' });
      return;
    }

    if (password !== confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match.' });
      return;
    }

    if (!validatePassword(password)) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters, include at least one uppercase letter and one number.' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await api.post('/api/new-auth/reset-password', {
        token,
        newPassword: password
      });

      setMessage({
        type: 'success',
        text: response.data?.message || 'Password reset successful.'
      });

      // Instantly clear out potentially sensitive data on success
      setPassword('');
      setConfirmPassword('');
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Invalid or expired password reset token.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/50 flex flex-col items-center justify-center px-4 py-8 sm:py-12">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl shadow-slate-100/80 border border-slate-200/60 overflow-hidden flex flex-col transform transition-all duration-200">
        
        {/* Header Block */}
        <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 px-6 py-6 text-white shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-xl">🔄</span>
            <h1 className="text-xl font-bold tracking-tight">Reset Password</h1>
          </div>
          <p className="mt-1 text-xs text-indigo-100/90 leading-relaxed">
            Configure secure, uncompromised access keys to replace temporary structural credentials.
          </p>
        </div>

        {/* Workspace Body */}
        <div className="p-6 space-y-5 overflow-y-auto">
          {!isTokenPresent && (
            <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-4 animate-in fade-in duration-200">
              <div className="flex gap-2.5 items-start">
                <span className="text-amber-500 text-base shrink-0 mt-0.5">⚠️</span>
                <div>
                  <h4 className="text-amber-800 text-xs font-bold uppercase tracking-wider">Verification Error</h4>
                  <p className="text-amber-700 text-xs mt-0.5 leading-relaxed">
                    The supplied contextual token link is invalid, unparsed, or timed out. Please request an authorized URL structure.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Large Hero Banner for Explicit Success State */}
          {message.type === 'success' && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-center space-y-2 animate-in zoom-in-95 duration-300">
              <span className="text-3xl block">🎉</span>
              <h3 className="text-emerald-900 font-bold text-sm tracking-tight">Password Reset Successful</h3>
              <p className="text-emerald-700 text-xs leading-relaxed max-w-xs mx-auto">
                {message.text || 'Your updates have synced successfully. You can now safe-close this configuration dashboard.'}
              </p>
            </div>
          )}

          {/* Form hidden when execution successfully ends to prevent dual execution patterns */}
          {message.type !== 'success' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* New Password Input Group */}
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                  New Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-slate-50/50 border border-slate-200 focus:bg-white rounded-xl px-3.5 py-2.5 pr-10 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-sm text-slate-800 placeholder-slate-400"
                    placeholder="Enter complex key string"
                    autoComplete="new-password"
                    disabled={loading || !isTokenPresent}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1 rounded-md"
                    disabled={loading || !isTokenPresent}
                  >
                    {showPassword ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" /></svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268-2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Confirm Password Input Group */}
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                  Confirm New Password
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full bg-slate-50/50 border border-slate-200 focus:bg-white rounded-xl px-3.5 py-2.5 pr-10 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-sm text-slate-800 placeholder-slate-400"
                    placeholder="Verify identical string match"
                    autoComplete="new-password"
                    disabled={loading || !isTokenPresent}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1 rounded-md"
                    disabled={loading || !isTokenPresent}
                  >
                    {showConfirmPassword ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" /></svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268-2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Micro Criteria Checkers Card */}
              <div className="bg-slate-50 border border-slate-100 rounded-xl p-3.5 space-y-2">
                <h4 className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Requirements Checklist</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                  <div className={`flex items-center gap-1.5 font-medium transition-colors ${criteria.hasLength ? 'text-emerald-700' : 'text-slate-500'}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${criteria.hasLength ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                    At least 8 characters
                  </div>
                  <div className={`flex items-center gap-1.5 font-medium transition-colors ${criteria.hasUpper ? 'text-emerald-700' : 'text-slate-500'}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${criteria.hasUpper ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                    1 Uppercase character
                  </div>
                  <div className={`flex items-center gap-1.5 font-medium transition-colors ${criteria.hasNumber ? 'text-emerald-700' : 'text-slate-500'}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${criteria.hasNumber ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                    1 Numeric entry
                  </div>
                  <div className={`flex items-center gap-1.5 font-medium transition-colors ${criteria.matches ? 'text-emerald-700' : 'text-slate-500'}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${criteria.matches ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                    Identical confirmation
                  </div>
                </div>
              </div>

              {/* Error Static Banner Only */}
              {message.type === 'error' && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 text-rose-800 p-3.5 text-xs font-medium leading-relaxed animate-in fade-in duration-150">
                  <div className="flex gap-2 items-start">
                    <span className="text-sm shrink-0 leading-none">❌</span>
                    <span>{message.text}</span>
                  </div>
                </div>
              )}

              {/* Action Button */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={loading || !isTokenPresent || !password || !confirmPassword}
                  className="w-full bg-indigo-600 text-white py-3 px-4 rounded-xl font-semibold hover:bg-indigo-700 active:bg-indigo-800 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-all text-sm shadow-md shadow-indigo-600/10"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-b-transparent" />
                      Updating Key...
                    </span>
                  ) : (
                    'Reset Password'
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;